import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, MessageCircle, Users } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { NavBar } from '../components/NavBar';

import { GroupHeader } from '../components/groups/GroupHeader';
import { GroupFeedTab } from '../components/groups/GroupFeedTab';
import { GroupChatTab } from '../components/groups/GroupChatTab';
import { GroupMembersTab } from '../components/groups/GroupMembersTab';

export const GroupDetailPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('feed');
  const [newPost, setNewPost] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [postImage, setPostImage] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const chatEndRef = useRef(null);

  const fetchGroup = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get(`/groups/${groupId}`);
      setGroup(res.data.group);
    } catch {
      toast.error('Failed to load group');
      navigate('/groups');
    }
    setLoading(false);
  }, [api, groupId, navigate]);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await api().get(`/groups/${groupId}/posts`);
      setPosts(res.data.posts || []);
    } catch { /* silent */ }
  }, [api, groupId]);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await api().get(`/groups/${groupId}/members`);
      setMembers(res.data.members || []);
    } catch { /* silent */ }
  }, [api, groupId]);

  const fetchChat = useCallback(async () => {
    try {
      const res = await api().get(`/groups/${groupId}/chat`);
      setChatMessages(res.data.messages || []);
    } catch { /* silent */ }
  }, [api, groupId]);

  useEffect(() => { fetchGroup(); }, [fetchGroup]);

  useEffect(() => {
    if (activeTab === 'feed') fetchPosts();
    if (activeTab === 'members') fetchMembers();
    if (activeTab === 'chat') fetchChat();
  }, [activeTab, fetchPosts, fetchMembers, fetchChat]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    if (activeTab === 'chat' && group?.is_member) {
      const interval = setInterval(fetchChat, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab, group?.is_member, fetchChat]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim() && !postImage) return;
    setPosting(true);
    try {
      let image_url = null;
      if (postImage) {
        setUploadingImage(true);
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(postImage);
        });
        const uploadRes = await api().post(`/groups/${groupId}/upload-image`, {
          data: base64Data, filename: postImage.name,
        });
        image_url = uploadRes.data.image_url;
        setUploadingImage(false);
      }
      const res = await api().post(`/groups/${groupId}/posts`, {
        content: newPost.trim() || ' ', image_url,
      });
      setPosts([res.data.post, ...posts]);
      setNewPost('');
      setPostImage(null);
      setPostImagePreview('');
      toast.success('Post created!');
    } catch {
      toast.error('Failed to create post');
      setUploadingImage(false);
    }
    setPosting(false);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum 10MB.');
      return;
    }
    setPostImage(file);
    setPostImagePreview(URL.createObjectURL(file));
  };

  const handleLikePost = async (postId) => {
    try {
      const res = await api().post(`/groups/${groupId}/posts/${postId}/like`);
      setPosts(posts.map((p) =>
        p.id === postId ? { ...p, user_liked: res.data.liked, likes_count: res.data.likes_count } : p
      ));
    } catch { toast.error('Failed to like post'); }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api().delete(`/groups/${groupId}/posts/${postId}`);
      setPosts(posts.filter((p) => p.id !== postId));
      toast.success('Post deleted');
    } catch { toast.error('Failed to delete post'); }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      const res = await api().post(`/groups/${groupId}/chat`, { content: newMessage.trim() });
      setChatMessages([...chatMessages, res.data.chat_message]);
      setNewMessage('');
    } catch { toast.error('Failed to send message'); }
    setSendingMessage(false);
  };

  const handleJoinGroup = async () => {
    try {
      await api().post(`/groups/${groupId}/join`);
      toast.success('Joined group!');
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join');
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm('Leave this group?')) return;
    try {
      await api().delete(`/groups/${groupId}/leave`);
      toast.success('Left group');
      navigate('/groups');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar />
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-black">
        <NavBar />
        <div className="text-center py-20">
          <p className="text-white">Group not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="group-detail-page">
      <NavBar />

      <GroupHeader group={group} user={user} onJoin={handleJoinGroup} onLeave={handleLeaveGroup} />

      <div className="border-b border-border sticky top-16 bg-black z-40">
        <div className="container mx-auto px-6">
          <div className="flex gap-6">
            {['feed', 'chat', 'members'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 font-medium capitalize transition-colors border-b-2 ${
                  activeTab === tab ? 'text-primary border-primary' : 'text-muted-foreground border-transparent hover:text-white'
                }`}
                data-testid={`group-tab-${tab}`}
              >
                {tab === 'chat' && <MessageCircle className="h-4 w-4 inline mr-1" />}
                {tab === 'members' && <Users className="h-4 w-4 inline mr-1" />}
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="container mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {activeTab === 'feed' && (
            <GroupFeedTab
              group={group} user={user} posts={posts}
              newPost={newPost} setNewPost={setNewPost}
              postImage={postImage} postImagePreview={postImagePreview}
              setPostImage={setPostImage} setPostImagePreview={setPostImagePreview}
              posting={posting} uploadingImage={uploadingImage}
              onCreatePost={handleCreatePost}
              onPhotoSelect={handlePhotoSelect}
              onLikePost={handleLikePost}
              onDeletePost={handleDeletePost}
            />
          )}
          {activeTab === 'chat' && (
            <GroupChatTab
              group={group} user={user} chatMessages={chatMessages}
              newMessage={newMessage} setNewMessage={setNewMessage}
              sendingMessage={sendingMessage}
              onSendMessage={handleSendMessage}
              onJoin={handleJoinGroup}
              chatEndRef={chatEndRef}
            />
          )}
          {activeTab === 'members' && <GroupMembersTab members={members} />}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default GroupDetailPage;
